import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// import { ExperienceEvents, HostEvents } from 'arise-bridge';
import { useAppDispatch } from '@/state/store';
import {
    selectInventoryItem,
    setIsNewsTickerVisible,
    setCurrentlyViewingMedia,
    setCurrentlyViewingPrize,
    setIsInventoryOpen,
    setIsSupplyRunLeaderboardOpen,
    setStartShare,
} from '@/state/features/app';
import { queueNewsTickerItem, setNewsTickerTag } from '@/state/features/feed';
import { useAriseBridge } from '@/arise/bridge/useAriseBridge';
import { AriseBridgeHost } from '@/arise/bridge/AriseBridgeHost';
import { failedGame, submitFeedUpdate, queueAchievement } from '@/state/features/app';
import LoadingScreen from '../LoadingScreen';
import { sendEvent } from '@/utils/analytics';
import { userEvent, tunaLab } from '@/arise/api';
import useTitanEvents from '@/components/IframeContainer/useTitanEvents';
import useHyperionEvents from '@/components/IframeContainer/useHyperionEvents';
import { homePath } from '@/theme/vars';
import { useNavigateToSignup } from '@/hooks/auth';
import DiscordAuthFlow, { DiscordAuthFlowMethods, QuestUnenrolledNotice } from '@/components/DiscordAuthFlow';
import { useMimasEvents } from '@/components/IframeContainer/useMimasEvents';
import { IS_DISCORD_ACTIVITY } from '@/globals';
import { NewsTickerItemData, NewsTickerItemDataIdOnly } from '@/models/feed';
import { isDiscordOnMobile, triggerDiscordShare } from '@/components/DiscordAuthFlow/useDiscord';

interface IframeContainerProps {
    src?: string;
    bridgeRef?: React.MutableRefObject<AriseBridgeHost>;
    arisewareProjectID?: string;
    onReady?: () => void;
    loadingImage?: string;
    loadingText?: { title: string; subtitle: string }[];
    forceInFrontOfUi?: boolean;
    style?: React.CSSProperties;
}

export default function IframeContainer({
    src,
    bridgeRef,
    arisewareProjectID,
    onReady,
    loadingImage,
    loadingText,
    forceInFrontOfUi,
    style,
}: IframeContainerProps) {
    const navigate = useNavigate();
    const { locationId } = useParams();
    const [iframeReady, setIframeReady] = useState(false);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    const discordRef = useRef<DiscordAuthFlowMethods>();

    let iframeUrl = useMemo(() => {
        const url = new URL(window.location.href);
        const iframeUrlParam = url.searchParams.get('iframeUrl');
        if (iframeUrlParam) {
            src = iframeUrlParam;
        }

        const params = new URLSearchParams(window.location.search);
        params.delete('iframeUrl');

        const paramsString = params.toString();
        if (paramsString) {
            src = `${src}?${paramsString}`;
        }
        return src;
    }, [src]);

    // If iframeUrl is passed as a query param, use that instead

    const dispatch = useAppDispatch();

    const bridge = useAriseBridge(iframeRef, {
        arisewareProjectID,
    });
    // Expose bridge to parent
    if (bridgeRef) {
        bridgeRef.current = bridge;
    }

    const titanEvents = useTitanEvents(arisewareProjectID);
    const hyperionEvents = useHyperionEvents();
    const navigateToSignup = useNavigateToSignup();
    const mimasEvents = useMimasEvents(discordRef, bridge);

    useEffect(() => {
        if (!bridge || !iframeUrl) return;

        function onReadyHandler(data: any) {
            setIframeReady(data.detail);
            if (data.detail === true) {
                onReady();
            }
        }

        function onFailed() {
            dispatch(failedGame({ arisewareProjectID }));
        }

        function onFeedUpdate(data: any) {
            dispatch(submitFeedUpdate(data.detail));
        }

        function onQueueAchievement(data: any) {
            dispatch(queueAchievement(data.detail));
        }

        function onSetNewsTickerVisibility(data: { detail: boolean }) {
            dispatch(setIsNewsTickerVisible(data.detail));
        }

        function onSetInventoryIsOpen(data: any) {
            dispatch(setIsInventoryOpen(data.detail));
        }

        function onShowSupplyRunLeaderboard(data: any) {
            // True or false
            dispatch(setIsSupplyRunLeaderboardOpen(data.detail));
        }

        function onSelectInventoryItem() {
            dispatch(selectInventoryItem());
        }

        function onTrack(data: any) {
            const { event, data: eventData } = data.detail;
            sendEvent(event, eventData);
        }

        // Tunalab is our internal event data gathering system
        function onTunaLab(data: any) {
            tunaLab(locationId, data.detail);
        }

        function onGotoHome() {
            navigate(homePath);
        }

        function onGotoSignup() {
            navigateToSignup();
        }

        function onViewMedia(data: any) {
            dispatch(setCurrentlyViewingMedia(data.detail));
        }

        function onViewPrize(data: any) {
            dispatch(setCurrentlyViewingPrize(data.detail));
        }

        function onCopyText(data: any) {
            try {
                navigator.clipboard.writeText(data.detail);
            } catch (error) {
                console.error('Bridge event copy-text could not write to clipboard: ', error);
            }
        }

        async function onSendUserEvent(data: { detail: NewsTickerItemData }) {
            const isNewsTickerDataIdOnlyItem = (item: NewsTickerItemData): item is NewsTickerItemDataIdOnly => {
                return 'id' in item;
            };

            const item = data?.detail;
            if (item) {
                dispatch(queueNewsTickerItem(item));
                if (isNewsTickerDataIdOnlyItem(item)) {
                    // If is ID only item, send to backend too
                    try {
                        await userEvent(locationId, item.id);
                    } catch (error) {
                        console.error('Bridge event sendUserEvent could not send event:', error);
                    }
                }
            }
        }

        function onSetNewsTickerTag(data: { detail: string }) {
            dispatch(setNewsTickerTag(data.detail));
        }

        function onShare(data: any) {
            if (IS_DISCORD_ACTIVITY && !isDiscordOnMobile()) {
                triggerDiscordShare();
            } else {
                const { message, url, title } = data.detail;
                dispatch(setStartShare([message, url, title]));
            }
        }

        bridge.on('ready', onReadyHandler);
        bridge.on('failed', onFailed);
        bridge.on('feed-update', onFeedUpdate);
        bridge.on('queue-achievement', onQueueAchievement);
        bridge.on('set-news-ticker-visibility', onSetNewsTickerVisibility);
        bridge.on('set-inventory-is-open', onSetInventoryIsOpen);
        bridge.on('show-supply-run-leaderboard', onShowSupplyRunLeaderboard);
        bridge.on('select-inventory-item', onSelectInventoryItem);
        bridge.on('track', onTrack);
        bridge.on('tunaLab', onTunaLab);
        bridge.on('goto_home', onGotoHome);
        bridge.on('goto_signup', onGotoSignup);
        bridge.on('view-media', onViewMedia);
        bridge.on('view-prize', onViewPrize);
        bridge.on('copy-text', onCopyText);
        bridge.on('send-user-event', onSendUserEvent);
        bridge.on('news-ticker-tag', onSetNewsTickerTag);
        bridge.on('share', onShare);

        if (process.env.PUBLIC_INSTANCE === 'titan') {
            bridge.on('goto_camp', titanEvents.onGotoCamp);
            bridge.on('goto', titanEvents.onGoto);
            bridge.on('submitresult', titanEvents.onSubmitResult);
        }

        if (process.env.PUBLIC_INSTANCE === 'hyperion') {
            bridge.on('goto', hyperionEvents.onGoto);
        }

        if (process.env.PUBLIC_INSTANCE === 'mimas') {
            bridge.on('ready', mimasEvents.pushCountryCode);
            bridge.on('open-url', mimasEvents.onOpenUrl);
            bridge.on('ariseware-checkpoint-reached', mimasEvents.onCheckpointReached);
        }

        return () => {
            dispatch(setIsNewsTickerVisible(true));
            bridge.off('ready', onReady);
            bridge.off('failed', onFailed);
            bridge.off('feed-update', onFeedUpdate);
            bridge.off('queue-achievement', onQueueAchievement);
            bridge.off('set-news-ticker-visibility', onSetNewsTickerVisibility);
            bridge.off('set-inventory-is-open', onSetInventoryIsOpen);
            bridge.off('show-supply-run-leaderboard', onShowSupplyRunLeaderboard);
            bridge.off('select-inventory-item', onSelectInventoryItem);
            bridge.off('track', onTrack);
            bridge.off('tunaLab', onTunaLab);
            bridge.off('goto_home', onGotoHome);
            bridge.off('goto_signup', onGotoSignup);
            bridge.off('view-media', onViewMedia);
            bridge.off('view-prize', onViewPrize);
            bridge.off('copy-text', onCopyText);
            bridge.off('send-user-event', onSendUserEvent);
            bridge.off('news-ticker-tag', onSetNewsTickerTag);
            bridge.off('share', onShare);

            if (process.env.PUBLIC_INSTANCE === 'titan') {
                bridge.off('goto_camp', titanEvents.onGotoCamp);
                bridge.off('goto', titanEvents.onGoto);
                bridge.off('submitresult', titanEvents.onSubmitResult);
            }
            if (process.env.PUBLIC_INSTANCE === 'hyperion') {
                bridge.off('goto', hyperionEvents.onGoto);
            }
            if (process.env.PUBLIC_INSTANCE === 'mimas') {
                bridge.off('open-url', mimasEvents.onOpenUrl);
                bridge.off('ariseware-checkpoint-reached', mimasEvents.onCheckpointReached);
                bridge.off('ready', mimasEvents.pushCountryCode);
            }
        };
    }, [bridge, iframeUrl]);

    const forceInFrontOfUIStyle: React.CSSProperties = forceInFrontOfUi ? { position: 'absolute', zIndex: 100000 } : {};

    return (
        <>
            <iframe
                allow="xr-spatial-tracking"
                ref={iframeRef}
                src={iframeUrl}
                style={{
                    width: '100%',
                    height: '100%',
                    opacity: iframeReady ? 1 : 0,
                    ...style,
                    ...forceInFrontOfUIStyle,
                }}
            />
            <LoadingScreen
                show={!iframeReady}
                backgroundImage={loadingImage}
                style={forceInFrontOfUIStyle}
                loadingText={loadingText}
            />
            {IS_DISCORD_ACTIVITY && <DiscordAuthFlow ref={discordRef} />}
        </>
    );
}
